import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { EditScheduleProps } from '../types';
import React, { useState } from 'react';
import globalStrings from '../../../resources/strings';

interface TimeInputProps {
    hours: Date[];
    value: Date;
    label: string;
    onChange: (hour: Date) => void;
}

const DEFAULT_HOUR = 12;
const HOUR_IN_MS = 1000 * 60 * 60;
const strings = globalStrings.chatSchedulePage.editSchedule;

function hoursForDay(day: Date) {
    let current = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const end = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
    const ret = [];
    while (current < end) {
        ret.push(current);
        current = new Date(current.valueOf() + HOUR_IN_MS);
    }
    return ret;
}

const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, hours, label }) => {
    const hourValue = new Date(value);
    hourValue.setMinutes(0, 0, 0);
    const minutes = value.getMinutes();
    const handleHourChange = (newHour: Date) => {
        onChange(
            new Date(
                newHour.getFullYear(),
                newHour.getMonth(),
                newHour.getDate(),
                newHour.getHours(),
                value.getMinutes(),
            ),
        );
    };
    const handleMinuteChange = (newMinutesString: string) => {
        let newMinutes = Math.min(Math.max(0, +newMinutesString), 59);
        if (isNaN(newMinutes)) newMinutes = 0;
        onChange(
            new Date(
                hourValue.getFullYear(),
                hourValue.getMonth(),
                hourValue.getDate(),
                hourValue.getHours(),
                newMinutes,
            ),
        );
    };
    return (
        <Box>
            <Typography sx={{ mb: 2 }} component="h3">
                {label}
            </Typography>
            <FormControl sx={{ width: 80 }}>
                <InputLabel>{strings.hour}</InputLabel>
                <Select
                    value={hourValue.valueOf()}
                    label="Hour"
                    onChange={(e) => handleHourChange(new Date(Number(e.target.value)))}
                >
                    {hours.map((h) => (
                        <MenuItem value={h.valueOf()} key={h.toUTCString()}>
                            {h.getHours()}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <TextField
                sx={{ width: 80 }}
                inputProps={{ inputMode: 'numeric', pattern: '\\d{2}', maxLength: 2 }}
                label={strings.minute}
                value={minutes}
                onChange={(e) => handleMinuteChange(e.target.value)}
            />
        </Box>
    );
};

const EditSchedule: React.FC<EditScheduleProps> = ({ day, initialValue, onSave, onCancel, disabled }) => {
    const defaultHour = new Date(day.getFullYear(), day.getMonth(), day.getDate(), DEFAULT_HOUR);
    const [startTime, setStartTime] = useState(initialValue?.startTime ?? defaultHour);
    const [endTime, setEndTime] = useState(initialValue?.endTime ?? defaultHour);
    const hours = hoursForDay(day);
    return (
        <Card sx={{ maxWidth: 500 }}>
            <CardContent>
                <form noValidate>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            flexDirection: 'row',
                            alignItems: 'flex-end',
                            gap: 5,
                        }}
                    >
                        <TimeInput hours={hours} value={startTime} onChange={setStartTime} label={strings.startTime} />
                        <TimeInput hours={hours} value={endTime} onChange={setEndTime} label={strings.endTime} />
                    </Box>
                </form>
            </CardContent>
            <CardActions>
                <Button type="button" onClick={() => onCancel()} size="large" variant="outlined" disabled={disabled}>
                    {strings.cancel}
                </Button>
                <Button
                    type="button"
                    onClick={() => onSave({ startTime, endTime })}
                    size="large"
                    variant="contained"
                    disabled={disabled}
                >
                    {strings.save}
                </Button>
            </CardActions>
        </Card>
    );
};
export default EditSchedule;
